import { Dialog } from "primereact/dialog";
import { SafeReportResponse } from "../Conversion";
import { useMemo, useState } from "react";
import { useToast } from "../../../context-providers/Toast";
import { Button } from "primereact/button";
import { generatePolicyFlyApplication } from "../../../api/routes";
import { useCalculatedPremium, useThisReportIsUpdating } from "../data";
import styles from "./PolicyFlyCard.module.css";
import {
  getEffectiveIncludedPerils,
  perilSentenceFromEnabledPerils,
} from "../general-input/util";
import {
  readableLayer,
  readableLimitOfLiabilityFromLayer,
  sortedLayersFromReport,
} from "../layers/util";
import * as client from "../../../client";
import { Message } from "primereact/message";
import { formatExactMoneyDollars } from "../Money";

type PolicyFlyModalProps = {
  specialtyPropertyInfo: SafeReportResponse;
  visible: boolean;
  setVisible: (value: boolean) => void;
};

export const PolicyFlyModal: React.FC<PolicyFlyModalProps> = ({
  specialtyPropertyInfo,
  visible,
  setVisible,
}) => {
  const [taskSubmitted, setTaskSubmitted] = useState(false);
  const toast = useToast();
  const companyInfo = specialtyPropertyInfo.report_json.company_info;
  const allPerils =
    specialtyPropertyInfo.report_json.policy_info?.included_perils ?? [];
  const perilSentenceOverride =
    specialtyPropertyInfo.report_json.policy_info?.peril_sentence_override;
  const allLayers = sortedLayersFromReport(specialtyPropertyInfo);

  const { data: premiumData } = useCalculatedPremium(
    specialtyPropertyInfo.report_id
  );

  // Build per-layer peril sentences for the API call and display
  const perilsByLayer = useMemo(() => {
    const result: Record<string, string> = {};
    for (const layer of allLayers) {
      if (!layer.is_enabled || !layer.layer_id) continue;
      const selectedSource = layer.selected_source ?? "rms";
      const effective = getEffectiveIncludedPerils(
        allPerils,
        premiumData,
        layer.layer_id,
        selectedSource
      );
      result[layer.layer_id] = perilSentenceFromEnabledPerils(
        effective,
        perilSentenceOverride
      );
    }
    return result;
  }, [allLayers, allPerils, perilSentenceOverride, premiumData]);
  const enabledLayers = allLayers.filter((layer) => layer.is_enabled);

  const policyFlyInfo = specialtyPropertyInfo.report_json.policy_fly_info;
  const applications = policyFlyInfo?.applications ?? [];

  // Build a lookup from layer_id to application for easy access
  const appByLayerId = useMemo(() => {
    const map = new Map<string, client.PolicyFlyApplication>();
    for (const app of applications) {
      map.set(app.layer_id, app);
    }
    return map;
  }, [applications]);

  const completedApplications = applications.filter((app) => !!app.url);
  const allLayersHaveUrls =
    enabledLayers.length > 0 &&
    enabledLayers.every((layer) => {
      const app = appByLayerId.get(layer.layer_id ?? "");
      return app?.url;
    });

  // Check if a Policy Fly task is already running via the generalized polling hook
  const isPolicyFlyRunning = useThisReportIsUpdating(
    specialtyPropertyInfo.report_id,
    "POLICY_FLY"
  );

  const isTaskActive = !!isPolicyFlyRunning || taskSubmitted;

  const handleCreate = async () => {
    setTaskSubmitted(true);
    try {
      await generatePolicyFlyApplication(
        specialtyPropertyInfo.report_id,
        perilsByLayer
      );
    } catch {
      toast.show({
        severity: "error",
        summary: "Failed to Generate Policy Fly Application",
        detail: "Could not start the Policy Fly application request.",
        sticky: true,
      });
    } finally {
      setTaskSubmitted(false);
    }
  };

  const layerForApp = (
    app: client.PolicyFlyApplication
  ): client.Layer | undefined => {
    return allLayers.find((l) => l.layer_id === app.layer_id);
  };

  return (
    <Dialog
      header="Policy Fly Application"
      visible={visible}
      style={{
        width: "calc(100vw - 72px)",
        height: "calc(100vh - 72px)",
      }}
      onHide={() => setVisible(false)}
      footer={
        <div>
          <Button
            label="Create Policy Fly application"
            loading={isTaskActive}
            onClick={handleCreate}
            disabled={allLayersHaveUrls || enabledLayers.length === 0}
          />
        </div>
      }
    >
      <div className={styles.verticalContainer}>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Named insured</span>
          <span>{companyInfo.company_name}</span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Mailing address</span>
          <span>{`${companyInfo.company_address}, ${companyInfo.company_city}, ${companyInfo.company_state}, ${companyInfo.company_zip}`}</span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Effective date</span>
          <span>{companyInfo.effective_date}</span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Expiration date</span>
          <span>{companyInfo.expiration_date}</span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Total insured value</span>
          <span>
            {formatExactMoneyDollars(
              specialtyPropertyInfo.precomputed_metadata.total_tiv
            )}
          </span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Layers</span>
          {enabledLayers.length === 0 && (
            <div>
              <Message severity="warn" text="Need at least 1 enabled layer" />
            </div>
          )}
          {enabledLayers.map((layer) => (
            <div key={layer.layer_id}>
              <span>{readableLimitOfLiabilityFromLayer(layer)}</span>
              <div
                style={{
                  fontSize: "0.85rem",
                  color: "var(--text-color-secondary)",
                }}
              >
                {perilsByLayer[layer.layer_id ?? ""] ?? ""}
              </div>
            </div>
          ))}
        </div>
        {completedApplications.length > 0 && (
          <div className={styles.verticalContainerSmall}>
            <span className={styles.header}>Policy Fly Applications</span>
            {completedApplications.map((app) => {
              const layer = layerForApp(app);
              const label = layer
                ? `${readableLayer(layer)} - ${app.suffix}`
                : `Layer ${app.suffix}`;
              return (
                <div>
                  <Button
                    key={app.layer_id}
                    label={label}
                    icon="pi pi-external-link"
                    outlined
                    onClick={() => {
                      window.open(app.url!, "_blank", "noopener,noreferrer");
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Dialog>
  );
};
